import { DashLanguages, normalizeDashLanguage } from "dash-common";

const STORAGE_KEY = "dash.portalLanguage";

type PickStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const readStored = (storage?: PickStorage): DashLanguages | null => {
  try {
    return normalizeDashLanguage(storage?.getItem(STORAGE_KEY)) ?? null;
  } catch {
    return null;
  }
};

/**
 * The language picked in this browser's switcher. It never touches the account,
 * and it is kept in memory as well, so a blocked localStorage still switches the
 * current tab.
 */
export const createPortalLanguagePick = (storage?: PickStorage) => {
  const listeners = new Set<() => void>();
  let current = readStored(storage);

  const update = (next: DashLanguages | null) => {
    current = next;
    try {
      if (next) storage?.setItem(STORAGE_KEY, next);
      else storage?.removeItem(STORAGE_KEY);
    } catch {
      // Private mode or blocked storage: the in-memory value still applies.
    }
    listeners.forEach((listener) => listener());
  };

  return {
    get: (): DashLanguages | null => current,
    set: (language: DashLanguages) => update(language),
    clear: () => update(null),
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
};

const browserStorage = (): PickStorage | undefined => {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
};

export const portalLanguagePick = createPortalLanguagePick(browserStorage());
